'use client';

import { useContext, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { FiTrash2 } from 'react-icons/fi';
import { IoClose } from 'react-icons/io5';

import { Modal, Spinner } from '@benzinga/core-ui';
import { SessionContext } from '@benzinga/session-context';
import { useWatchlistContext } from '@benzinga/user-context';
import { WatchlistManager, WatchlistSymbol } from '@benzinga/watchlist-manager';
import { PortfolioModalHeader } from './PortfolioModalHeader';
import {
  portfolioModalBodyClass,
  portfolioModalContainerStyle,
  portfolioModalFooterClass,
  portfolioModalShellClass,
} from './modalStyles';

export const EditWatchlist = () => {
  const { editModal, setDeleteModal, setEditModal } = useWatchlistContext();
  const session = useContext(SessionContext);
  const watchlistManager = session.getManager(WatchlistManager);
  const [watchlistName, setWatchlistName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [removingTicker, setRemovingTicker] = useState<string | null>(null);

  const closeModal = () => {
    if (isSaving || removingTicker) return;
    setEditModal({ show: false, watchlist: null });
  };

  useEffect(() => {
    if (editModal.show) {
      setWatchlistName(editModal.watchlist?.name ?? '');
    }
  }, [editModal.show, editModal.watchlist?.name]);

  const saveWatchlist = async () => {
    const watchlist = editModal.watchlist;
    const trimmedName = watchlistName.trim();
    if (!watchlist || !trimmedName) return;
    if (trimmedName === watchlist.name) {
      closeModal();
      return;
    }

    setIsSaving(true);
    try {
      const result = await watchlistManager.updateWatchlistSettings({ ...watchlist, name: trimmedName });
      if (result.err) {
        toast.error('Unable to rename watchlist. Please try again.');
        return;
      }
      toast.success(`Watchlist renamed to ${trimmedName}`);
      setEditModal({ show: false, watchlist: null });
    } catch (error) {
      console.error('Error renaming watchlist:', error);
      toast.error('Unable to rename watchlist. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const removeTicker = async (symbol: WatchlistSymbol) => {
    const watchlist = editModal.watchlist;
    if (!watchlist) return;

    setRemovingTicker(symbol.symbol);
    try {
      const result = await watchlistManager.removeTickersFromWatchlist(watchlist, [symbol]);
      if (result.err) {
        toast.error(`Unable to remove ${symbol.symbol}. Please try again.`);
        return;
      }
      toast.success(`${symbol.symbol} removed from ${watchlist.name}`);
    } catch (error) {
      console.error('Error removing ticker from watchlist:', error);
      toast.error(`Unable to remove ${symbol.symbol}. Please try again.`);
    } finally {
      setRemovingTicker(null);
    }
  };

  const confirmDelete = () => {
    if (!editModal.watchlist) return;
    setDeleteModal({ show: true, watchlist: editModal.watchlist, isLoading: false });
    setEditModal({ show: false, watchlist: null });
  };

  const tickerCount = editModal.watchlist?.symbols.length ?? 0;

  return (
    <Modal
      visible={editModal.show}
      onClose={closeModal}
      disableFullScreenButton={true}
      hideTitle={true}
      fullScreen={true}
      size={{ manual: { width: '520px', maxWidth: 'calc(100vw - 32px)', height: 'auto' } }}
      containerStyle={portfolioModalContainerStyle}
    >
      <div className={portfolioModalShellClass}>
        <PortfolioModalHeader title="Edit watchlist" onClose={closeModal} />
        <div className={`${portfolioModalBodyClass} flex flex-col gap-5`}>
          <div>
            <label className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500" htmlFor="edit-watchlist-name">
              Name
            </label>
            <input
              id="edit-watchlist-name"
              type="text"
              maxLength={80}
              value={watchlistName}
              disabled={isSaving}
              className="min-h-11 w-full rounded-lg border border-slate-200 bg-white px-3.5 py-2.5 text-sm font-semibold text-slate-950 focus:border-bzblue-500 focus:outline-none focus:ring-2 focus:ring-bzblue-100"
              onChange={event => setWatchlistName(event.target.value)}
              onKeyDown={event => {
                if (event.key === 'Enter') saveWatchlist();
              }}
            />
          </div>

          <div>
            <div className="mb-1.5 flex items-center justify-between">
              <p className="text-xs font-bold uppercase tracking-wide text-slate-500">Tickers</p>
              <span className="rounded-md bg-slate-100 px-2 py-0.5 text-xs font-bold text-slate-500">{tickerCount}</span>
            </div>
            <div className="flex max-h-56 flex-wrap gap-2 overflow-y-auto rounded-lg border border-slate-200 bg-slate-50 p-3.5">
              {tickerCount > 0 ? (
                editModal.watchlist?.symbols.map(symbol => (
                  <span
                    key={symbol.symbolId}
                    className="inline-flex h-8 items-center gap-1.5 rounded-md bg-white px-2.5 text-xs font-bold text-slate-800 shadow-sm ring-1 ring-slate-200"
                  >
                    {symbol.symbol}
                    <button
                      type="button"
                      aria-label={`Remove ${symbol.symbol}`}
                      disabled={removingTicker !== null}
                      className="rounded p-0.5 text-slate-400 hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-50"
                      onClick={() => removeTicker(symbol)}
                    >
                      {removingTicker === symbol.symbol ? <Spinner size={14} /> : <IoClose size={14} />}
                    </button>
                  </span>
                ))
              ) : (
                <p className="w-full py-8 text-center text-sm text-slate-500">No tickers in this watchlist yet.</p>
              )}
            </div>
          </div>
        </div>

        <div className={`${portfolioModalFooterClass} sm:justify-between`}>
          <button
            type="button"
            className="inline-flex min-h-10 items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-semibold text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
            disabled={isSaving || removingTicker !== null}
            onClick={confirmDelete}
          >
            <FiTrash2 size={15} />
            Delete watchlist
          </button>
          <div className="flex gap-2">
            <button
              type="button"
              className="min-h-10 flex-1 rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 sm:flex-none"
              disabled={isSaving || removingTicker !== null}
              onClick={closeModal}
            >
              Cancel
            </button>
            <button
              type="button"
              className="inline-flex h-10 flex-1 items-center justify-center gap-2 rounded-md bg-bzblue-700 px-4 text-sm font-bold text-white hover:bg-bzblue-800 disabled:cursor-not-allowed disabled:opacity-50 sm:flex-none"
              disabled={isSaving || removingTicker !== null || !watchlistName.trim()}
              onClick={saveWatchlist}
            >
              {isSaving ? <Spinner size={16} /> : 'Save changes'}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};
